class BabyIvfFull extends HTMLElement {
  connectedCallback() {
    if (this.dataset.ready === 'true') return;
    this.dataset.ready = 'true';
    this.form = this.querySelector('[data-ivf-form]');
    this.result = this.querySelector('[data-ivf-result]');
    this.error = this.querySelector('[data-ivf-error]');
    this.savedStatus = this.querySelector('[data-ivf-saved]');
    if (!this.form || !this.result) return;

    this.form.addEventListener('submit', (event) => this.calculate(event));
    this.querySelector('[data-ivf-save]')?.addEventListener('click', () => this.saveTimeline());
    this.querySelector('[data-ivf-recalculate]')?.addEventListener('click', () => this.recalculate());
    this.restoreSavedInput();
  }

  parseDate(value) {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(value || '')) return null;
    const [year, month, day] = value.split('-').map(Number);
    const date = new Date(Date.UTC(year, month - 1, day));
    return date.getUTCFullYear() === year && date.getUTCMonth() === month - 1 && date.getUTCDate() === day ? date : null;
  }

  addDays(date, days) {
    const next = new Date(date.getTime());
    next.setUTCDate(next.getUTCDate() + days);
    return next;
  }

  formatDate(date, short = false) {
    return new Intl.DateTimeFormat('en-US', {
      timeZone: 'UTC', month: short ? 'short' : 'long', day: 'numeric', year: 'numeric',
    }).format(date);
  }

  showError(message) {
    this.error.textContent = message;
    this.error.hidden = false;
  }

  calculate(event) {
    event?.preventDefault();
    this.error.hidden = true;
    this.savedStatus.hidden = true;
    if (!this.form.reportValidity()) return;
    const transferDate = this.parseDate(this.form.elements.transferDate.value);
    const embryoAge = Number(this.form.elements.embryoAge.value);
    if (!transferDate || ![3, 5].includes(embryoAge)) {
      this.showError('Select a valid transfer date and embryo age.');
      return;
    }

    const offset = embryoAge === 5 ? 261 : 263;
    const dueDate = this.addDays(transferDate, offset);
    const pregnancyStart = this.addDays(dueDate, -280);
    const conceptionDate = this.addDays(transferDate, -embryoAge);
    this.currentCalculation = {
      embryoAge,
      transferDate: this.form.elements.transferDate.value,
      dueDate: dueDate.toISOString().slice(0, 10),
    };

    this.querySelector('[data-ivf-due-date]').textContent = this.formatDate(dueDate);
    this.querySelector('[data-ivf-method-summary]').textContent = `Day ${embryoAge} embryo transfer on ${this.formatDate(transferDate, true)}`;
    this.querySelector('[data-ivf-start]').textContent = this.formatDate(pregnancyStart, true);
    this.querySelector('[data-ivf-conception]').textContent = this.formatDate(conceptionDate, true);
    this.querySelector('[data-ivf-full-term]').textContent = this.formatDate(this.addDays(dueDate, -21), true);

    const milestones = [
      ['Week 4', this.addDays(pregnancyStart, 28), 'Early pregnancy milestone'],
      ['Week 6', this.addDays(pregnancyStart, 42), 'Typical early scan window'],
      ['Week 12', this.addDays(pregnancyStart, 84), 'End of first-trimester screening window'],
      ['Week 20', this.addDays(pregnancyStart, 140), 'Typical anatomy scan window'],
      ['Week 28', this.addDays(pregnancyStart, 196), 'Third trimester begins'],
      ['Week 37', this.addDays(pregnancyStart, 259), 'Full term begins'],
      ['Week 40', dueDate, 'Estimated due date'],
    ];
    const timeline = this.querySelector('[data-ivf-timeline]');
    timeline.replaceChildren(...milestones.map(([week, date, label]) => {
      const item = document.createElement('li');
      const weekElement = document.createElement('span');
      const dateElement = document.createElement('strong');
      const labelElement = document.createElement('small');
      weekElement.textContent = week;
      dateElement.textContent = this.formatDate(date, true);
      labelElement.textContent = label;
      item.append(weekElement, dateElement, labelElement);
      return item;
    }));
    this.result.hidden = false;
    this.result.scrollIntoView({ behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth', block: 'nearest' });
  }

  saveTimeline() {
    if (!this.currentCalculation) return;
    try {
      localStorage.setItem('littleNestIvfTimeline', JSON.stringify({
        ...this.currentCalculation,
        savedAt: new Date().toISOString(),
      }));
      this.savedStatus.textContent = 'Timeline saved on this device.';
    } catch (error) {
      this.savedStatus.textContent = 'Your browser blocked local saving. You can still screenshot this timeline.';
    }
    this.savedStatus.hidden = false;
  }

  restoreSavedInput() {
    try {
      const saved = JSON.parse(localStorage.getItem('littleNestIvfTimeline') || 'null');
      if (!saved?.transferDate || ![3, 5].includes(Number(saved.embryoAge))) return;
      this.form.elements.transferDate.value = saved.transferDate;
      const method = this.form.querySelector(`[name="embryoAge"][value="${Number(saved.embryoAge)}"]`);
      if (method) method.checked = true;
    } catch (error) {
      // Keep the calculator usable when storage is unavailable or contains invalid data.
    }
  }

  recalculate() {
    this.result.hidden = true;
    this.form.scrollIntoView({ behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth', block: 'center' });
    this.form.elements.transferDate.focus({ preventScroll: true });
  }
}

if (!customElements.get('baby-ivf-full')) {
  customElements.define('baby-ivf-full', BabyIvfFull);
}
